import React, { useState, useEffect } from 'react';
import { isEmpty, isNil, pathOr } from 'ramda';
import { useQuery, useMutation } from '@apollo/react-hooks';
import shortid from 'shortid';
import {
  AppProvider,
  Frame,
  Page,
  Card,
  ResourceList,
  ResourceItem,
  Checkbox,
  EmptyState,
  Stack,
  Badge,
  Button,
  Select,
  Toast,
  TextStyle,
  Icon,
  FormLayout,
  Modal,
  TextField,
} from '@shopify/polaris';
import moment from 'moment';
import Loader from './SkeletonLoader';
import { EditMajorMonotone, DeleteMajorMonotone, TickMinor, CancelSmallMinor, FlagMajorMonotone } from '@shopify/polaris-icons';
import { TitleBar } from '@shopify/app-bridge-react';
import enTranslations from '@shopify/polaris/locales/en.json';
import { GET_QUESTIONNAIRE, GET_CURRENT_SUBSCRIPTION } from '../apollo/queries';
import { UPDATE_QUESTIONNAIRE, CREATE_QUESTIONNAIRE } from '../apollo/mutation';

const listResourceName = { singular: 'question', plural: 'questions' };
const isNilOrEmpty = (value) => isNil(value) || isEmpty(value);

const Questionnaire = (props) => {
  const { match = {}, shopifyToken, history = {}, shopifyDomain } = { ...props };
  const { params = {} } = { ...match };
  const { id: questionnaireId = '' } = { ...params };
  const { push: changeRoute = () => {} } = { ...history };
  const isNew = !questionnaireId;

  const [form, setForm] = useState({});
  const [question, setQuestion] = useState({});
  const [toastStatus, setToastStatus] = useState({ status: false, message: '' });
  const [saving, setSaving] = useState(false);
  const [archiveModal, setArchiveModal] = useState(false);
  const [addModal, setAddModal] = useState(false);
  const [questionId, setQuestionId] = useState();

  const { loading, data = {}, refetch } = useQuery(GET_QUESTIONNAIRE, {
    variables: { shopifyToken, questionnaireId },
  });
  const { data: subscriptionData } = useQuery(GET_CURRENT_SUBSCRIPTION, { variables: { shopifyToken } });
  const [updateQuestionnaire] = useMutation(UPDATE_QUESTIONNAIRE);
  const [createQuestionnaire] = useMutation(CREATE_QUESTIONNAIRE);

  const { shop = {} } = { ...subscriptionData };
  const { currentSubscription = {} } = { ...shop };
  const isSubscribedUser = !isNilOrEmpty(currentSubscription);
  const { questionnaire } = { ...data };

  const isAllowedUser =
    isSubscribedUser &&
    (currentSubscription.subscriptionType === 'Premium' ||
      currentSubscription.subscriptionType === 'Growth' ||
      currentSubscription.subscriptionType === 'Business');

  const isSaveBarActive = isAllowedUser && !!form?.name && (form?.fields || []).length > 0;
  const toggleArchiveModal = () => setArchiveModal(!archiveModal);

  const updateQuestionnaireSuccess = () => {
    setSaving(false);
    setToastStatus({ status: true, message: 'The questionnaire was updated successfully.' });
    refetch();
  };

  const createQuestionnaireSuccess = (response) => {
    const { data: createResponse = {} } = { ...response };
    const { createQuestionnaire: createData = {} } = { ...createResponse };
    const { questionnaire = {} } = { ...createData };
    const { id = '' } = { ...questionnaire };
    setSaving(false);
    setToastStatus({ status: true, message: 'The questionnaire was created successfully.' });
    changeRoute(`/questionnaire/${id}`);
  };

  const onFailure = () => {
    setSaving(false);
    setToastStatus({ status: true, message: 'There is an error with the server' });
  };

  const onUpdateStatus = async (status) => {
    setSaving(true);
    setArchiveModal(false);
    const template = { ...questionnaire };
    delete template.id;
    delete template.totalJobs;
    const response = await updateQuestionnaire({
      variables: {
        ...template,
        status,
        shopifyToken,
        questionnaireId,
      },
    });
    const errors = pathOr([], ['data', 'updateQuestionnaire', 'errors'])(response);
    const emptyErrors = isEmpty(errors);
    if (emptyErrors) {
      updateQuestionnaireSuccess();
    } else {
      onFailure();
    }
  };

  const onUpdateQuestionnaire = () => {
    setSaving(true);

    updateQuestionnaire({
      variables: { shopifyToken, questionnaireId, ...form, status: form?.status || 'published' },
    })
      .then(updateQuestionnaireSuccess)
      .catch(onFailure);
  };

  const onCreateQuestionnaire = (duplicate = false) => {
    setSaving(true);

    let variables = { shopifyToken, ...form, status: form?.status || 'published' };
    if (duplicate) {
      delete variables.id;
      variables = { ...variables, name: `Copy of ${form?.name}` };
    }
    createQuestionnaire({ variables }).then(createQuestionnaireSuccess).catch(onFailure);
  };

  const toggleAddModal = () => setAddModal(!addModal);

  useEffect(() => {
    if (!!questionnaire) {
      setForm({ name: questionnaire?.name, status: questionnaire?.status, fields: questionnaire?.fields });
    }
  }, [data]);

  if (loading || saving) {
    return (
      <AppProvider>
        <Loader />
      </AppProvider>
    );
  }
  const questionTitle = (type) => {
    switch (type) {
      case 'boolean':
        return 'Boolean (Yes/No)';
      case 'references':
        return 'References';
      case 'paragraph':
        return 'Paragraph';
      case 'multiple-choice':
        return 'Multiple choice';
      case 'video-references':
        return 'Video response';
    }
  };
  const onRenderListItems = (item) => {
    const { id, label, type, isKO } = item;
    return (
      <div key={id} className='job-application-row' style={{ padding: '20px 0' }}>
        <div style={{ flexGrow: 1 }}>
          <p style={{ maxWidth: '90%' }}>{label}</p>
          <Stack spacing={'tight'}>
            <TextStyle variation='subdued'>{questionTitle(type)}</TextStyle>
            {isKO && <Icon source={FlagMajorMonotone} color={'critical'} />}
          </Stack>
        </div>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <span
            style={{ width: '30px', display: 'inline-block', cursor: 'pointer' }}
            onClick={() => {
              setQuestionId(id);
              setQuestion({
                ...item,
              });
              toggleAddModal();
            }}>
            <Icon source={EditMajorMonotone} />
          </span>
          <span
            style={{ width: '30px', display: 'inline-block', marginRight: '8px', cursor: 'pointer' }}
            onClick={() => {
              const updatedQuestions = (form?.fields || []).filter(({ id: item }) => item !== id);
              setForm({ ...form, fields: updatedQuestions });
            }}>
            <Icon source={DeleteMajorMonotone} />
          </span>
        </div>
      </div>
    );
  };

  const defaultOptions = [
    { value: '', id: shortid.generate() },
    { value: '', id: shortid.generate(), isCorrect: true },
    { value: '', id: shortid.generate() },
  ];

  return (
    <AppProvider i18n={enTranslations}>
      <Frame>
        <TitleBar title='Questionnaires' breadcrumbs={[{ content: 'Settings', onAction: () => { history.push('/settings'); } }]} />
        <Page
        breadcrumbs={[{ content: 'Questionnaires', onAction: () => { history.push('/settings/templates/questionnaires'); } }]}
          title={isNew ? 'Create questionnaire' : questionnaire?.name}
          titleMetadata={
            !isNew ? (
              <Badge status={questionnaire?.status === 'published' ? 'success' : 'default'}>
                <span className={'capitalize'}>{questionnaire?.status}</span>
              </Badge>
            ) : null
          }
          subtitle={isNew ? '' : `Updated ${moment(form?.updatedAt).format('MMMM D, YYYY')}`}
          primaryAction={
            isAllowedUser
              ? {
                  content: isNew ? 'Save' : 'Update',
                  onAction: isNew ? () => onCreateQuestionnaire(false) : onUpdateQuestionnaire,
                  disabled: !isSaveBarActive,
                }
              : undefined
          }
          secondaryActions={
            isAllowedUser
              ? [
                  {
                    content: 'Duplicate',
                    disabled: isNew,
                    onAction: () => onCreateQuestionnaire(true),
                  },
                  {
                    content: 'Preview',
                    disabled: isNew,
                    onAction: () => window.open(`https://${shopifyDomain}/a/careers/preview-questionnaire?q=${questionnaireId}`),
                  },
                ]
              : undefined
          }>
          <Card>
            {isAllowedUser ? (
              <>
                <Card.Section>
                  <TextField
                    id='name'
                    label='Name'
                    value={form?.name}
                    name='name'
                    placeholder={'Questionnaire name'}
                    onChange={(name) => setForm({ ...form, name })}
                  />
                </Card.Section>
                <Card.Section title={(form?.fields || []).length > 0 ? 'Custom questions' : ''}>
                  <ResourceList
                    resourceName={listResourceName}
                    items={form?.fields || []}
                    emptyState={
                      <EmptyState heading='No questions yet'>
                        <Stack vertical>
                          <p>Get started adding your first question</p>
                          <Button primary onClick={toggleAddModal}>
                            Add question
                          </Button>
                        </Stack>
                      </EmptyState>
                    }
                    hasMoreItems={false}
                    showHeader={false}
                    renderItem={onRenderListItems}
                  />
                  {(form?.fields || []).length > 0 && (
                    <>
                      <br />
                      <div className='left-hand-footer'>
                        <div></div>
                        <Button onClick={toggleAddModal} style={{ display: 'inline-flex', cursor: 'pointer', marginTop: '20px' }}>
                          Add question
                        </Button>
                      </div>
                    </>
                  )}
                </Card.Section>
              </>
            ) : (
              <EmptyState heading='Upgrade your account plan'>
                <Stack vertical>
                  <p>Questionnaires are a paid feature for Growth, Business and Premium account plans</p>
                  <Button primary onClick={() => changeRoute('/account-plan')}>
                    Upgrade account plan
                  </Button>
                </Stack>
              </EmptyState>
            )}
          </Card>
          {toastStatus.status && (
            <Toast content={toastStatus.message} onDismiss={() => setToastStatus({ status: false, message: '' })} />
          )}
          <div className='career-portal__form-buttons'>
            <div style={{ display: 'flex', flexDirection: 'row' }}>
              <div style={{ marginRight: '10px' }}>
                <Button onClick={() => changeRoute('/settings/templates/questionnaires')}>Cancel</Button>
              </div>

              {isAllowedUser && (
                <Button
                  destructive={questionnaire?.status === 'published'}
                  outline
                  onClick={toggleArchiveModal}
                  disabled={!!questionnaire?.totalJobs}>
                  {questionnaire?.status === 'published' ? 'Archive' : 'Unarchive'} questionnaire
                </Button>
              )}
            </div>

            <Button
              primary
              disabled={!isSaveBarActive}
              onClick={isNew ? () => onCreateQuestionnaire(false) : onUpdateQuestionnaire}>
              {isNew ? 'Save' : 'Update'}
            </Button>
          </div>
        </Page>
      </Frame>
      <Modal
        open={addModal}
        onClose={toggleAddModal}
        title={!!questionId ? 'Update question' : 'Add question'}
        primaryAction={{
          content: `${questionId ? 'Update' : 'Add'} question`,
          onAction: () => {
            if (!!questionId) {
              const updatedQuestions = (form?.fields || []).map((q) => {
                if (q.id === questionId) {
                  return {
                    ...q,
                    ...question,
                  };
                }
                return q;
              });
              setForm({ ...form, fields: updatedQuestions });
            } else {
              setForm({
                ...form,
                fields: [
                  ...(form?.fields || []),
                  {
                    id: shortid.generate(),
                    ...question,
                  },
                ],
              });
            }
            setQuestionId(null);
            setQuestion({});
            toggleAddModal();
          },
          disabled:
            !question.label || !question.type || (question.type === 'multiple-choice' && (question?.options || []).length === 0),
        }}
        secondaryActions={[
          {
            content: 'Cancel',
            onAction: () => {
              setQuestion({});
              setQuestionId(null);
              toggleAddModal();
            },
          },
        ]}
        loading={false}>
        <Modal.Section>
          <FormLayout>
            <Select
              label='Question type'
              placeholder={'Select a type ...'}
              options={[
                { label: 'Boolean (Yes/No)', value: 'boolean' },
                { label: 'Multiple choice', value: 'multiple-choice' },
                { label: 'Paragraph', value: 'paragraph' },
                { label: 'References', value: 'references' },
                { label: 'Video Response', value: 'video-references' },
              ]}
              onChange={(type) =>
                setQuestion({
                  ...question,
                  type,
                  isTrue: type === 'boolean' ? (question.isTrue === undefined ? true : question.isTrue) : undefined,
                  isKO: type === 'references' || type === 'paragraph' ? false : question.isKO,
                  references: type === 'references' ? '1' : undefined,
                  options:
                    type === 'multiple-choice'
                      ? question?.options === undefined
                        ? defaultOptions
                        : question?.options
                      : undefined,
                })
              }
              value={question.type}
            />
            <TextField
              id='label'
              label='Question'
              value={question.label}
              name='label'
              onChange={(label) => setQuestion({ ...question, label })}
              multiline={4}
            />
            {question.type === 'references' && (
              <div>
                <Select
                  label='Number of references'
                  options={Array(10)
                    .fill('el')
                    .map((el, index) => ({ label: `${index + 1}`, value: `${index + 1}` }))}
                  onChange={(references) =>
                    setQuestion({
                      ...question,
                      references,
                    })
                  }
                  value={question.references}
                />
                <br />
                <ResourceList
                  items={['First name', 'Last name', 'Company', 'Email', 'Phone']}
                  renderItem={(item, index) => {
                    return (
                      <ResourceItem id={index}>
                        <h3>
                          <TextStyle variation='subdued'>{item}</TextStyle>
                        </h3>
                      </ResourceItem>
                    );
                  }}
                />
              </div>
            )}
            {question.type !== 'references' && question.type !== 'video-references' && question.type !== 'paragraph' && (
              <div>
                <Checkbox
                  label={
                    <Stack spacing={'tight'}>
                      <Icon source={FlagMajorMonotone} color={'critical'} />
                      <p>Knockout question</p>
                    </Stack>
                  }
                  checked={question.isKO}
                  onChange={(isKO) => setQuestion({ ...question, isKO })}
                />
              </div>
            )}
            {question.type === 'boolean' && (
              <>
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'row',
                    borderBottom: '1px solid #c2c2c2',
                    padding: '15px 0px',
                  }}>
                  <span style={{ flexGrow: 1 }}>Yes</span>
                  <span
                    style={{
                      width: '30px',
                      cursor: 'pointer',
                    }}
                    onClick={() => setQuestion({ ...question, isTrue: true })}>
                    <Icon
                      source={question.isTrue ? TickMinor : CancelSmallMinor}
                      color={question.isTrue ? 'success' : 'critical'}
                    />
                  </span>
                </div>
                <div style={{ display: 'flex', flexDirection: 'row', padding: '15px 0px' }}>
                  <span style={{ flexGrow: 1 }}>No</span>
                  <span
                    style={{
                      width: '30px',
                      cursor: 'pointer',
                    }}
                    onClick={() => setQuestion({ ...question, isTrue: false })}>
                    <Icon
                      source={!question.isTrue ? TickMinor : CancelSmallMinor}
                      color={!question.isTrue ? 'success' : 'critical'}
                    />
                  </span>
                </div>
              </>
            )}
            {question.type === 'multiple-choice' && (
              <div>
                {(question?.options || []).map(({ id, value, isCorrect }, index) => (
                  <div
                    style={{
                      marginBottom: '10px',
                      display: 'grid',
                      alignItems: 'center',
                      gridTemplateColumns: '90% 5% 5%',
                    }}
                    key={id}>
                    <TextField
                      id={id}
                      label={`Option ${index + 1}`}
                      value={value}
                      onChange={(option) => {
                        const opts = [...(question?.options || [])];
                        opts[index].value = option;
                        setQuestion({ ...question, options: opts });
                      }}
                    />
                    <span
                      style={{
                        width: '30px',
                        display: 'inline-block',
                        margin: '20px 0 0 10px',
                        cursor: 'pointer',
                      }}
                      onClick={() => {
                        const opts = [...(question?.options || [])];
                        opts[index].isCorrect = !opts[index].isCorrect;
                        opts.forEach((a, b) => {
                          if (b !== index) {
                            opts[b].isCorrect = false;
                          }
                        });
                        setQuestion({ ...question, options: opts });
                      }}>
                      <Icon source={isCorrect ? TickMinor : CancelSmallMinor} color={isCorrect ? 'success' : 'critical'} />
                    </span>
                    <span
                      style={{ width: '30px', display: 'inline-block', margin: '20px 0 0 10px', cursor: 'pointer' }}
                      onClick={() => {
                        setQuestion({
                          ...question,
                          options: (question?.options || []).filter((option) => option.id !== id),
                        });
                      }}>
                      <Icon source={DeleteMajorMonotone} />
                    </span>
                  </div>
                ))}

                <Button
                  size={'slim'}
                  onClick={() => {
                    setQuestion({
                      ...question,
                      options: [...(question?.options || []), { value: '', id: shortid.generate() }],
                    });
                  }}>
                  Add option
                </Button>
              </div>
            )}
          </FormLayout>
        </Modal.Section>
      </Modal>
      <Modal
        open={archiveModal}
        onClose={toggleArchiveModal}
        title={`${questionnaire?.status === 'published' ? 'Archive' : 'Unarchive'} questionnaire`}
        primaryAction={{
          content: `Yes, ${questionnaire?.status === 'archived' ? 'un' : ''}archive`,
          onAction: () => onUpdateStatus(questionnaire?.status === 'published' ? 'archived' : 'published'),
        }}
        secondaryActions={[
          {
            content: 'Cancel',
            onAction: toggleArchiveModal,
          },
        ]}
        loading={false}>
        <Modal.Section>
          <p>
            {`${questionnaire?.status === 'published' ? 'Archiving' : 'Unarchiving'} this questionnaire will ${
              questionnaire?.status === 'published' ? 'hide' : 'show'
            } from your questionnaire templates list page and the questionnaire template list on job setup. You'll find it using the status filter in your questionnaire list.`}
          </p>
        </Modal.Section>
      </Modal>
    </AppProvider>
  );
};

export default Questionnaire;
